import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout, {
  AuthCard,
  CornerLogo,
  GoogleButton,
  OrDivider,
  AuthInput,
  PrimaryButton,
  AuthLink,
  AUTH,
} from './AuthLayout';

interface Props {
  onRegister: (email: string, password: string, displayName: string) => Promise<void>;
}

export default function RegisterPage({ onRegister }: Props) {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async () => {
    setError('');
    if (!displayName.trim() || !email.trim() || !password) {
      setError('All fields are required');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords don\u2019t match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await onRegister(email.trim(), password.trim(), displayName.trim());
      // Preserve upgrade/redirect params from pricing page or shared invite flow
      const params = new URLSearchParams(window.location.search);
      const upgrade = params.get('upgrade');
      const cycle = params.get('cycle');
      const redirect = params.get('redirect');
      if (upgrade) {
        // Skip signature setup — go straight to app with upgrade params for immediate Stripe redirect
        navigate(`/app?upgrade=${upgrade}&cycle=${cycle || 'monthly'}`, { replace: true });
      } else if (redirect && redirect.startsWith('/')) {
        navigate(redirect, { replace: true });
      } else {
        navigate('/auth/signature-setup');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Registration failed';
      if (msg === 'EMAIL_EXISTS') {
        setError('An account with this email already exists');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    window.location.href = '/api/auth/google';
  };

  return (
    <AuthLayout>
      <AuthCard>
        {/* Header */}
        <div
          style={{
            padding: '32px 32px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            borderBottom: `1px solid ${AUTH.sectionBorder}`,
          }}
        >
          <CornerLogo />
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: AUTH.textPrimary,
              letterSpacing: '-0.02em',
              textAlign: 'center',
              lineHeight: '24px',
            }}
          >
            Get your work done faster.
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 400,
              color: AUTH.textSecondary,
              textAlign: 'center',
              lineHeight: '16px',
            }}
          >
            Create a free account to save your work and pick up where you left off.
          </p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {/* Google + divider */}
        <div
          style={{
            padding: '20px 32px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <GoogleButton onClick={handleGoogle} />
          <OrDivider />
        </div>

        {/* Form fields */}
        <div
          style={{
            padding: '0 32px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <AuthInput
            label="Display name"
            placeholder="Your name"
            value={displayName}
            onChange={setDisplayName}
          />
          <AuthInput
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={setEmail}
          />
          <AuthInput
            label="Password"
            type="password"
            placeholder=""
            value={password}
            onChange={setPassword}
          />
          <AuthInput
            label="Confirm password"
            type="password"
            placeholder=""
            value={confirmPassword}
            onChange={setConfirmPassword}
            error={passwordMismatch ? 'Passwords don\u2019t match' : undefined}
          />
        </div>

        {error && !passwordMismatch && (
          <p style={{ margin: 0, padding: '8px 32px 0', fontSize: 12, color: AUTH.textError, textAlign: 'center' }}>
            {error}
          </p>
        )}

        {/* CTA + footer */}
        <div
          style={{
            padding: '16px 32px 28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <PrimaryButton disabled={loading} type="submit">
            {loading ? 'Creating account\u2026' : 'Create account \u2192'}
          </PrimaryButton>
          <p style={{ margin: 0, fontSize: 12, color: AUTH.textSecondary, lineHeight: '16px' }}>
            Already have an account?{' '}
            <AuthLink onClick={() => navigate('/auth/login')}>Sign in</AuthLink>
          </p>
          <AuthLink
            onClick={() => {
              localStorage.setItem('corner:onboarding-complete', '1');
              const params = new URLSearchParams(window.location.search);
              const upgrade = params.get('upgrade');
              const cycle = params.get('cycle');
              navigate(upgrade ? `/app?upgrade=${upgrade}&cycle=${cycle || 'monthly'}` : '/app');
            }}
            color={AUTH.textSecondary}
            weight={400}
          >
            Continue without account
          </AuthLink>
        </div>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}
